/**
 * PuffyAir, single page script. No dependencies.
 *
 * Three jobs:
 *   1. Attribution capture, so a COD order can be traced back to the ad.
 *   2. Scroll-driven reveal, without IntersectionObserver.
 *   3. The order form layer, opened by the fixed button.
 *   4. FAQ: one question open at a time.
 *
 * Every scroll listener is passive and registered in the CAPTURE phase on
 * document. Horizon scrolls inside .page-wrapper with html and body at
 * overflow: hidden, so a listener on window never fires here. This is not a
 * precaution, it is a bug that already cost two dead components.
 */

(() => {
  'use strict';

  const REDUCED = matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ------------------------------------------------------ 1. attribution -- */

  const ATTR_KEY = 'puffy_attr';
  const ATTR_FIELDS = [
    'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term',
    'ttclid', 'fbclid', 'gclid',
  ];

  /**
   * The Releasit form bypasses Shopify checkout, so campaign data does not
   * travel with the order by itself. Capture on landing, keep it for the whole
   * session, and only overwrite a stored value when the new URL actually
   * carries one: a visitor who navigates to a clean URL mid-session must not
   * lose the campaign that brought them.
   */
  function captureAttribution() {
    let stored = {};
    try {
      stored = JSON.parse(sessionStorage.getItem(ATTR_KEY) || '{}');
    } catch {
      stored = {};
    }

    const params = new URLSearchParams(location.search);
    let changed = false;

    for (const field of ATTR_FIELDS) {
      const value = params.get(field);
      if (value && value !== stored[field]) {
        stored[field] = value;
        changed = true;
      }
    }

    if (!stored.landing_page) {
      stored.landing_page = location.pathname;
      stored.landed_at = new Date().toISOString();
      changed = true;
    }

    if (changed) {
      try {
        sessionStorage.setItem(ATTR_KEY, JSON.stringify(stored));
      } catch {
        /* Private mode. Attribution is lost, the order still goes through. */
      }
    }

    return stored;
  }

  const attribution = captureAttribution();

  /**
   * Releasit renders its form after page load, and re-renders it when the
   * quantity changes. Rather than racing it, re-fill on every mutation of the
   * form container. Cheap: the field is a single hidden input.
   */
  function fillAttributionField(root) {
    const forms = (root || document).querySelectorAll('form');
    const serialised = Object.entries(attribution)
      .map(([k, v]) => `${k}=${v}`)
      .join(' | ');

    for (const form of forms) {
      if (!form.querySelector('[name*="properties"], [name*="attributes"], input, textarea')) continue;

      // Releasit rebuilds its form after we inject, which left two identical
      // Sursa inputs in the order section. Keep the first, drop the rest: a
      // duplicated line-item attribute is the kind of thing that turns into a
      // malformed order note nobody notices until they read the export.
      const existing = form.querySelectorAll('input[name="attributes[Sursa]"]');
      let field = existing[0];
      for (let i = 1; i < existing.length; i += 1) existing[i].remove();

      if (!field) {
        field = document.createElement('input');
        field.type = 'hidden';
        field.name = 'attributes[Sursa]';
        form.appendChild(field);
      }
      field.value = serialised;
    }
  }

  fillAttributionField();

  /**
   * Reumplerea se face la un cadru după ce s-au liniștit mutațiile, nu la
   * fiecare.
   *
   * Formularul Releasit e o aplicație React care rescrie DOM-ul de sute de ori
   * la randare. Un observator pe tot `body` care rula `querySelectorAll('form')`
   * la fiecare mutație costa, măsurat cu Lighthouse, 460ms de fir principal la
   * încărcarea paginii. Cu o singură reluare pe cadru, rezultatul e identic:
   * câmpul e completat înainte ca cineva să apuce să trimită.
   */
  let formPending = 0;
  const formObserver = new MutationObserver(() => {
    if (formPending) return;
    formPending = requestAnimationFrame(() => {
      formPending = 0;
      fillAttributionField();
    });
  });
  formObserver.observe(document.body, { childList: true, subtree: true });

  // Exposed so the Releasit embed, or a later pixel helper, can read it.
  window.PuffyAttribution = attribution;

  /* ----------------------------------------------------------- 2. reveal -- */

  /**
   * Scroll-driven rather than IntersectionObserver: IO misses elements on the
   * programmatic jump an anchor link performs, and the hero CTA is exactly
   * such a jump. A plain position check cannot miss anything.
   */
  function initReveal() {
    const items = [...document.querySelectorAll('.puffy-reveal')];
    if (!items.length) return;

    document.documentElement.setAttribute('data-puffy-reveal', '');

    if (REDUCED) {
      items.forEach((el) => el.classList.add('is-in'));
      return;
    }

    let pending = false;

    // Content must never be permanently invisible because a script stalled.
    // If the sweep has not cleared everything within a few seconds, show the
    // rest outright: a missed animation is a cosmetic loss, a blank section is
    // a lost sale.
    const failsafe = setTimeout(() => {
      items.forEach((el) => el.classList.add('is-in'));
      items.length = 0;
    }, 4000);

    const sweep = () => {
      pending = false;
      // Reveal as the element enters the lower third, not as it nearly fills
      // the screen: on tall sections a 0.88 threshold fires so late the
      // transition is never seen.
      const limit = innerHeight * 0.98;
      for (let i = items.length - 1; i >= 0; i--) {
        if (items[i].getBoundingClientRect().top < limit) {
          items[i].classList.add('is-in');
          items.splice(i, 1);
        }
      }
      if (!items.length) {
        clearTimeout(failsafe);
        document.removeEventListener('scroll', queue, true);
        removeEventListener('resize', queue);
      }
    };

    const queue = () => {
      if (pending) return;
      pending = true;
      requestAnimationFrame(sweep);
    };

    document.addEventListener('scroll', queue, { capture: true, passive: true });
    addEventListener('resize', queue, { passive: true });

    // Double rAF: with a single frame the hidden and shown states collapse
    // into one paint and nothing transitions, leaving everything at opacity 0.
    requestAnimationFrame(() => requestAnimationFrame(sweep));
  }

  /* --------------------------------------------- 3. order form layer ----- */

  /**
   * The order form lives in a layer over the page, opened by the fixed button.
   * Everything about it is here: opening, closing, the Escape key, keeping the
   * page behind from scrolling, and returning focus to the button afterwards.
   */
  /**
   * Ce anume deschide formularul.
   *
   * Pe lângă butonul fix, și legăturile din pagină care duceau spre blocul de
   * cumpărare. Butonul din hero e un bloc standard al temei, cu adresă pusă din
   * editor; nu îi pot adăuga un atribut fără să modific un bloc folosit peste
   * tot, așa că e recunoscut după adresă.
   */
  const OPENERS = '[data-puffy-open-order], a[href="#comanda"], a[href="#cumpara"], a[href="#cumpara-jos"]';

  function initOrderDialog() {
    const dialog = document.querySelector('[data-puffy-order-dialog]');
    const openers = document.querySelectorAll(OPENERS);
    if (!dialog || !openers.length) return;

    const panel = dialog.querySelector('[role="dialog"]');
    let lastFocus = null;

    const focusable = () =>
      [...panel.querySelectorAll('a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')]
        .filter((el) => el.offsetParent !== null);

    function open(event) {
      // Legăturile din pagină ar sări în plus la ancoră, peste fereastra tocmai
      // deschisă, și ar lăsa pagina derulată în alt loc când se închide.
      if (event && event.type === 'click') event.preventDefault();

      lastFocus = document.activeElement;
      dialog.hidden = false;

      // The page behind must not scroll under the layer. position: fixed on the
      // body would jump to the top, so the scroll position is held and restored.
      const scroller = document.querySelector('.page-wrapper') || document.scrollingElement;
      dialog.dataset.restore = String(scroller.scrollTop);
      document.documentElement.style.overflow = 'hidden';

      const first = focusable()[0];
      if (first) first.focus({ preventScroll: true });
    }

    function close() {
      if (dialog.hidden) return;
      dialog.hidden = true;
      document.documentElement.style.overflow = '';
      if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
    }

    openers.forEach((btn) => btn.addEventListener('click', open));

    dialog.addEventListener('click', (event) => {
      if (event.target.closest('[data-puffy-order-close]')) close();
    });

    document.addEventListener('keydown', (event) => {
      if (dialog.hidden) return;

      if (event.key === 'Escape') {
        close();
        return;
      }

      // Focus stays inside the layer while it is open. Without this the keyboard
      // walks out into the page behind, which is invisible and unusable.
      if (event.key !== 'Tab') return;
      const items = focusable();
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
  }

  /* ---------------------------------------- 3b. apariția butonului fix --- */

  /**
   * Butonul nu există de la începutul paginii. Apare exact când hero-ul 3D
   * și-a terminat pista de derulare, adică atunci când secțiunea lui a ieșit
   * complet deasupra ecranului. Până atunci hero-ul are propriul CTA, iar două
   * butoane suprapuse pe primul ecran se anulează reciproc.
   *
   * Listener în faza de capture pe document: Horizon derulează în
   * .page-wrapper, cu html și body pe overflow: hidden, deci un listener pe
   * window nu s-ar declanșa niciodată aici.
   */
  function initCtaBar() {
    const bar = document.querySelector('[data-puffy-cta-bar]');
    if (!bar) return;

    const hero = document.querySelector('hero-sequence, .puffy-hero');
    let pending = false;

    const update = () => {
      pending = false;
      // Fără hero în pagină butonul stă vizibil de la început: e cazul
      // paginilor fără hero, unde nu are ce să aștepte.
      const past = hero ? hero.getBoundingClientRect().bottom <= 0 : true;
      bar.toggleAttribute('data-visible', past);
    };

    const queue = () => {
      if (pending) return;
      pending = true;
      requestAnimationFrame(update);
    };

    document.addEventListener('scroll', queue, { capture: true, passive: true });
    addEventListener('resize', queue, { passive: true });
    update();
  }

  /* ------------------------------------------------- 4. FAQ, o singură --- */

  /**
   * O singură întrebare deschisă odată.
   *
   * HTML-ul are deja mecanismul, atributul `name` pe <details>, dar tema
   * randează acordeonul fără el și nu toate browserele în circulație îl
   * respectă încă. Un ascultător pe faza de capture face acelaşi lucru peste
   * tot: `toggle` nu urcă în arbore, de aceea capture și nu bubbling.
   */
  function initFaq() {
    const groups = document.querySelectorAll('.accordion');
    if (!groups.length) return;

    groups.forEach((group) => {
      const panels = [...group.querySelectorAll('details')];
      if (panels.length < 2) return;

      group.addEventListener('toggle', (event) => {
        const opened = event.target;
        if (!opened.open || !panels.includes(opened)) return;
        panels.forEach((panel) => {
          if (panel !== opened) panel.open = false;
        });
      }, true);
    });
  }

  /**
   * NU se pune `content-visibility: auto` pe secțiuni.
   *
   * A fost construit și măsurat pe magazin. Pe hârtie e cel mai mare câștig de
   * aici: costul unui recalcul complet de layout scădea de la 6,45ms la 1,64ms,
   * adică 75% mai puțin, exact bugetul care lipsește pe un telefon lent.
   *
   * În practică a ieșit mai rău decât problema pe care o rezolva. La derulare
   * rapidă, secțiunile apăreau goale, ca niște panouri bej fără conținut, până
   * când derularea se oprea și browserul apuca să le picteze. Verificat că nu e
   * artefact de captură: aceeași zonă, aceeași derulare, cu regula scoasă se
   * randează complet. Pe o pagină care primește trafic plătit rece, un vizitator
   * care vede secțiuni goale pleacă mai repede decât unul care vede o derulare
   * ușor sacadată.
   *
   * `contain: layout style`, varianta fără sărirea randării, a fost și ea
   * măsurată: 7,71ms față de 6,36ms de bază, adică nimic. Nu există cale de
   * mijloc, deci nu se pune nimic.
   */

  const start = () => {
    initReveal();
    initCtaBar();
    initOrderDialog();
    initFaq();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
